import { GROUPS } from '../data/board.js';
import { invariant } from './GameError.js';

export function ownsCompleteGroup(game, playerId, group) {
  const groupIds = GROUPS[group] ?? [];
  return groupIds.length > 0 && groupIds.every((id) => game.getProperty(id)?.ownerId === playerId);
}

export function calculateRent(game, propertyId, diceTotal = game.dice.total) {
  const property = game.getProperty(propertyId);
  const cell = game.board[propertyId];
  if (!property || !property.ownerId || property.mortgaged) return 0;

  if (cell.type === 'street') {
    if (property.houses > 0) return cell.rents[property.houses];
    const base = cell.rents[0];
    return ownsCompleteGroup(game, property.ownerId, cell.group) ? base * 2 : base;
  }

  const owner = game.getPlayer(property.ownerId);
  const ownedOfType = owner.properties.filter((id) => game.board[id].type === cell.type).length;
  if (cell.type === 'transport') {
    return cell.rents[Math.max(0, ownedOfType - 1)];
  }
  if (cell.type === 'utility') {
    return diceTotal * cell.rents[Math.min(ownedOfType - 1, 1)];
  }
  return 0;
}

export function assertBuildAllowed(game, playerId, propertyId) {
  const property = game.getProperty(propertyId);
  const cell = game.board[propertyId];
  invariant(game.status === 'active', 'GAME_NOT_ACTIVE', 'Матч ещё не начался или уже завершён.');
  invariant(property && cell?.type === 'street', 'NOT_BUILDABLE', 'На этом объекте нельзя строить.');
  invariant(property.ownerId === playerId, 'NOT_OWNER', 'Этот объект вам не принадлежит.');
  invariant(ownsCompleteGroup(game, playerId, cell.group), 'NO_MONOPOLY', 'Для строительства нужна вся цветовая группа.');
  const groupProperties = GROUPS[cell.group].map((id) => game.getProperty(id));
  invariant(groupProperties.every((item) => !item.mortgaged), 'GROUP_MORTGAGED', 'Сначала выкупите все объекты группы из ипотеки.');
  invariant(property.houses < 5, 'HOTEL_ALREADY_BUILT', 'На объекте уже есть отель.');
  const minimum = Math.min(...groupProperties.map((item) => item.houses));
  invariant(property.houses === minimum, 'UNEVEN_BUILD', 'Стройте равномерно: сначала улучшите остальные улицы группы.');
  const player = game.getPlayer(playerId);
  invariant(player.money >= cell.buildCost, 'NOT_ENOUGH_MONEY', 'Недостаточно денег для строительства.');
  return { property, cell, player };
}

export function assertSellBuildingAllowed(game, playerId, propertyId) {
  const property = game.getProperty(propertyId);
  const cell = game.board[propertyId];
  invariant(property && cell?.type === 'street', 'NOT_BUILDABLE', 'На этом объекте нет построек.');
  invariant(property.ownerId === playerId, 'NOT_OWNER', 'Этот объект вам не принадлежит.');
  invariant(property.houses > 0, 'NO_BUILDINGS', 'На объекте нет построек.');
  const groupProperties = GROUPS[cell.group].map((id) => game.getProperty(id));
  const maximum = Math.max(...groupProperties.map((item) => item.houses));
  invariant(property.houses === maximum, 'UNEVEN_SELL', 'Продавайте постройки равномерно по группе.');
  return { property, cell, player: game.getPlayer(playerId) };
}

export function groupHasBuildings(game, group) {
  return (GROUPS[group] ?? []).some((id) => game.getProperty(id)?.houses > 0);
}

export function liquidationCapacity(game, playerId) {
  const player = game.getPlayer(playerId);
  return player.properties.reduce((total, id) => {
    const property = game.getProperty(id);
    const cell = game.board[id];
    const mortgage = property.mortgaged ? 0 : cell.mortgageValue;
    const buildings = cell.type === 'street' ? Math.floor((property.houses * cell.buildCost) / 2) : 0;
    return total + mortgage + buildings;
  }, player.money);
}
